import type {
    ClientLaravelErrorKey,
    ClientLaravelErrorTuple,
    ClientValidationErrorKey,
    ServerErrorResponse,
    ServerErrors,
} from '@/company-portal/add-file/add-file-form-errors.types';
import { CLIENT_VALIDATION_ERROR_KEYS, HANDLED_ERROR } from '@/company-portal/add-file/add-file-form-errors.types';
import type { ClientValidationErrorKeys, TypedClientError } from '@/components/Views/Company/File/Client/client-info-form.types';
import toastr from 'toastr';
import { reactive } from 'vue';
import type { ClientInfoFormRowIndex } from './add-file.types';

export const clientInfoErrors = reactive(new Map<ClientInfoFormRowIndex, TypedClientError>());

/**
 * Extracts the field name from a dotted server error key.
 *
 * 'email.0' => 'email'
 * 'role.1'  => 'role'
 */
export const getErrorField = (key: ClientLaravelErrorKey): ClientValidationErrorKeys => {
    return key.split('.')[0] as ClientValidationErrorKeys;
};

/**
 * Extracts the form row index from a dotted server error key.
 *
 * 'email.0' => '0'
 * 'phone.2' => '2'
 */
export const getFormRowIndex = (key: ClientLaravelErrorKey): ClientInfoFormRowIndex => {
    return `${key.split('.')[1]}`;
};

/**
 * Filters server errors down to entries whose key contains the given field name.
 *
 * filterErrorsByKey({ 'email.0': ['Email is required.'], 'role.0': ['Role is invalid.'] }, 'email')
 * => [['email.0', ['Email is required.']]]
 */
export const filterErrorsByKey = (errors: ServerErrors, key: ClientValidationErrorKey): ClientLaravelErrorTuple[] => {
    return Object.entries(errors).filter((error): error is ClientLaravelErrorTuple => error[0].includes(key));
};

/**
 * Writes error messages from server error tuples into clientInfoErrors.
 *
 * setClientInfoErrors([['email.0', ['Email is required.']]])
 * => clientInfoErrors.get('0').email === 'Email is required.'
 */
export const setClientInfoErrors = (errorTuples: ClientLaravelErrorTuple[]) => {
    errorTuples.forEach(error => {
        const [errorKey, errorValueArray] = error;
        const formRowIndex = getFormRowIndex(errorKey);
        const clientInfoError = clientInfoErrors.get(formRowIndex);
        if (clientInfoError) {
            clientInfoError[getErrorField(errorKey)] = errorValueArray.join('');
        }
    });
};

/**
 * Extracts email, phone, and role errors from a server error response
 * and writes them into clientInfoErrors for display in the client form.
 */
export const handleClientInfoErrors = (errors: ServerErrors) => {
    for (const key of CLIENT_VALIDATION_ERROR_KEYS) {
        setClientInfoErrors(filterErrorsByKey(errors, key));
    }
};

export function handleServerErrorResponse(
    jqXHR: JQueryXHR<ServerErrorResponse> | Pick<JQueryXHR<ServerErrorResponse>, 'status' | 'responseJSON'>,
    textStatus: JQuery.Ajax.ErrorTextStatus | typeof HANDLED_ERROR,
    formId: JQuery.Selector = '#addClientForm',
    scrollToError = true,
) {
    if (jqXHR.status === 422) {
        // 422 = error in form input
        toastr.error('Please correct form error(s)', 'Error(s)!', { timeOut: 5000 });
        const errors: ServerErrors = jqXHR.responseJSON?.errors ?? {};
        handleClientInfoErrors(errors);
        clearFormErrors(formId);

        $.each(errors, function (key, val) {
            const inputSelector = $(formId + ' [data-id="' + key + '"]');
            const errorSelector = $(formId + ' [data-id="' + key + '.error"]');
            // For errors that have visible error elements, show error in the error element
            // Otherwise, show the error in a block (e.g. hidden email input errors)
            if (errorSelector.is(':visible') && errorSelector.length) {
                inputSelector.addClass('error');
                errorSelector.addClass('error').html(val.join(''));
                if (scrollToError) {
                    scrollToElement('.error');
                }
            } else {
                $(formId + ' #errorBlockForHiddenOrMissingElements').append('<div>' + val + '</div>');
            }
        });

        // Display separate error message for missing information for 'cm' orders
        $.each(['vendor_order_id', 'vendor_transaction_id'], function (key, val) {
            if (val in errors) {
                toastr.error(errors[val].join(''), 'Fatal Error', { timeOut: 0 });
            }
        });
    } else if (textStatus === HANDLED_ERROR) {
        return; // no-op, the calling code will handle logging the error/updating the UI
    } else if (textStatus === 'timeout') {
        toastr.error('Request timed out. Please try again', 'Error!', { timeOut: 5000 });
    } else if (jqXHR.responseJSON?.message) {
        toastr.error(jqXHR.responseJSON.message, 'Error!', { timeOut: 8000 });
    } else {
        toastr.error('Unknown error. Please try again', 'Error!', { timeOut: 5000 });
    }
}

export function clearFormErrors(formId: JQuery.Selector = '#addClientForm') {
    $('.form-control-error-description').text('');
    $(formId + ' .error').removeClass('error');
    $(formId + ' [data-id$=".error"]').text(''); // clear error messages from server
    $(formId + ' label[id$="-error"][for^="email"]').remove();
}

function scrollToElement(element: JQuery.Selector) {
    if (typeof element === 'string') {
        element += ':visible';
    }
    const $selector = $(element);
    if ($selector.length) {
        const offset = $selector.first().offset();
        if (offset) {
            $([document.documentElement, document.body]).animate(
                {
                    scrollTop: offset.top - 220,
                },
                700,
            );
        }
    }
}

export function clearDocumentErrorsOnSuccessfulTab(
    $documentContainer: JQuery<HTMLElement>,
    clearDocumentErrors: (errorElement: JQuery<HTMLElement>, errorClass: string) => void,
) {
    const errorClasses = ['document-error', 'signers-error', 'document-signers-error'];
    errorClasses.forEach(function (errorClass) {
        clearDocumentErrors($documentContainer.find(`.${errorClass}`), errorClass);
    });
}
